import { Button } from "ant-design-vue";
import lodash from "lodash";
import moment from "moment";
import { defineAsyncComponent } from "vue";
import {
    createColumnInlineInputNumber,
    createColumnInlineRadioGroup,
    createColumnInlineSelect,
    createColumnInlineTextArea,
    createColumnInput,
    createColumnInputNumber
} from "../../../../../components/table/c-table.logic";
import i18n from "../../../../../i18n";
import store from "../../../../../store";
import { conditionFormChecker, onClickDeleteAllClient } from "../../../../../utils/form";
import { createLoading } from "../../../../../utils/loading";
import { createManageDelete, createManageExportCsv, createPage } from "../../../../../utils/page";
import { sortBy } from "../../../../../utils/search";

const CIcon = defineAsyncComponent(() => import("../../../../../components/icon/CIcon"));
const Number = defineAsyncComponent(() => import("../../../../../components/table/custom-render/Number"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/transaction/hazMgtStorageLocationStore";

export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/search`, { criteria });

export const callHazFrom = ({ searchQuery }) =>
    store.dispatch("resource/v1/master/lookup/cacheFindAllByGroup", { group: "HAZ_FROM", value: searchQuery });
export const callHazImportProduct = ({ searchQuery, refHazId, refHazSubStanceId }) => {
    const where = [
        { name: "refHazMainSubStance.refHaz.id", type: "UUID", operator: "=", value: refHazId },
        { name: "refHazMainSubStance.refSubStance.id", type: "UUID", operator: "=", value: refHazSubStanceId }
    ];
    if (searchQuery) {
        where.push({ name: "name", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/hazImportProduct/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "refHaz.*"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "refHaz.approveCode", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callStroageLocation = ({ searchQuery, refHazId, refUserCompany }) => {
    const where = [
        { name: "isActive", type: "Boolean", operator: "=", value: true },
        { name: "isTemp", type: "Boolean", operator: "=", value: false },
        { name: "refStatus.key", type: "String", operator: "=", value: "complete" },
        { name: "expireDate", type: "Date", operator: ">=", value: moment().format("YYYY-MM-DD") },
        { name: "isLocation", type: "Boolean", operator: "=", value: true }
    ];
    if (refHazId) {
        where.push({ name: "items.refHaz.id", type: "UUID", operator: "=", value: refHazId });
    }
    if (refUserCompany) {
        where.push({ name: "refUserCompany.id", type: "UUID", operator: "=", value: refUserCompany });
    }
    if (searchQuery) {
        where.push({ name: "name", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/storageLocation/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "name"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "name", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callStroageLocationSub = ({ searchQuery, refStorageLocationId, refHazId }) => {
    const where = [
        { name: "refStorageLocation.id", type: "UUID", operator: "=", value: refStorageLocationId },
        { name: "items.refHaz.id", type: "UUID", operator: "=", value: refHazId }
    ];
    if (searchQuery) {
        where.push({ name: "buildingName", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/storageLocationSub/search", {
            criteria: {
                isDistinct: true,
                select: ["id", "version", "buildingName", "useKg", "useKgReserve", "useKgUse"],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "buildingName", type: "ASC" }]
            }
        })
        .then(page => page.data);
};
export const callStroageLocationSubItem = ({ searchQuery, refStorageLocationSubId, refHazId, notIds }) => {
    const where = [
        { name: "refStorageLocationSub.id", type: "UUID", operator: "=", value: refStorageLocationSubId },
        { name: "refHaz.id", type: "UUID", operator: "=", value: refHazId },
        { name: "refHazLicense.refOperation.key", type: "String", operator: "in", value: ["1", "5"] }
    ];
    if (notIds?.length > 0) {
        where.push({ name: "id", type: "UUID", operator: "!in", value: notIds });
    }
    if (searchQuery) {
        where.push({ name: "lot", type: "String", operator: "like", value: searchQuery });
    }
    return store
        .dispatch("resource/v1/transaction/storageLocationSubItem/search", {
            criteria: {
                isDistinct: true,
                select: [
                    "id",
                    "version",
                    "lot",
                    "reserveIn",
                    "reserveOut",
                    "amount",
                    "refHazLicense.id",
                    "refHazLicense.version",
                    "refHazLicense.refOperation.id",
                    "refHazLicense.refOperation.version",
                    "refHazLicense.refOperation.key",
                    "refHazLicense.refOperation.value"
                ],
                page: 0,
                size: 1000000,
                where,
                sort: [{ name: "lot", type: "ASC" }]
            }
        })
        .then(page => page.data);
};

export const createColumns = ({ props, data }) => {
    const { viewOnly, itemsKey } = props;
    const { useForm } = data;
    const { modelRef /* , initialModel */ } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    //const itemOlds = itemsKey ? lodash.get(initialModel, itemsKey) : modelRef;
    const columns = [];
    columns.push(
        createColumnInputNumber({
            dataIndexValue: ({ index }) => {
                return data.page.page * data.page.size + index + 1;
            },
            customRender: ({ record, index, column }) => {
                const records = sortBy({ list: items, sort: data.page.sort });
                const recordBefore = records[index - 1];
                let button = (
                    <Button
                        class="c-btn-create btn-success"
                        size="small"
                        onClick={() => {
                            let nextIndex = index;
                            records.forEach((d, index2) => {
                                if (record.refHazSubStance.id === d.refHazSubStance.id) {
                                    nextIndex = index2;
                                }
                            });
                            items.splice(nextIndex + 2, 0, {
                                refHazSubStance: record.refHazSubStance,
                                intense: record.intense,
                                isImport: record.isImport,
                                isInternal: record.isInternal
                            });
                        }}
                    >
                        <CIcon file="plus.svg"></CIcon>
                    </Button>
                );
                if ((recordBefore && recordBefore.refHazSubStance.id === record.refHazSubStance.id) || viewOnly) {
                    button = <div></div>;
                }
                return (
                    <>
                        {button}
                        <span class="ml-2">{column.dataIndexValue({ record, index, column })}</span>
                    </>
                );
            },
            sorter: false,
            width: 100
        })
    );
    columns.push(
        createColumnInput({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refHazSubStance"),
            dataIndex: "refHazSubStance.name",
            customRender: ({ record, index, text }) => {
                const records = sortBy({ list: items, sort: data.page.sort });
                const recordBefore = records[index - 1];
                if (recordBefore && recordBefore.refHazSubStance.id === record.refHazSubStance.id) {
                    return null;
                }
                return text;
            },
            sorter: false,
            width: 200
        })
    );
    columns.push(
        createColumnInputNumber({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.intense"),
            dataIndex: "intense",
            customRender: param => {
                const { record, index } = param;
                const records = sortBy({ list: items, sort: data.page.sort });
                const recordBefore = records[index - 1];
                if (recordBefore && recordBefore.refHazSubStance.id === record.refHazSubStance.id) {
                    return null;
                }
                return <Number {...param.column} {...param}></Number>;
            },
            sorter: false,
            width: 50,
            allowPrecision: true
        })
    );
    columns.push(
        createColumnInlineRadioGroup({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refHazFrom"),
            dataIndex: "refHazFrom",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly,
                        list: ({ searchQuery }) =>
                            callHazFrom({ searchQuery }).then(items =>
                                items.filter(item => [record.isImport ? "1" : undefined, record.isInternal ? "2" : undefined].includes(item.key))
                            ),
                        optLabel: "value",
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refHazFrom = value;
                            record.refHaz = null;
                            record.refStorageLocation = null;
                            record.refStorageLocationSub = null;
                            record.refStorageLocationSubItem = null;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false,
            width: 250
        })
    );
    columns.push(
        createColumnInlineSelect({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refHaz"),
            dataIndex: "refHaz",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || record.refHazFrom?.key !== "1",
                        list: ({ searchQuery }) => {
                            return callHazImportProduct({
                                searchQuery,
                                refHazId: props.hazMgtStorageLocation.refHaz.id,
                                refHazSubStanceId: record.refHazSubStance.id
                            }).then(items => items.map(item => item.refHaz));
                        },
                        optLabel: "approveCode",
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refHaz = value;
                            record.refStorageLocation = null;
                            record.refStorageLocationSub = null;
                            record.refStorageLocationSubItem = null;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false
        })
    );
    columns.push(
        createColumnInlineSelect({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refStorageLocation"),
            dataIndex: "refStorageLocation",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || !record.refHaz,
                        list: ({ searchQuery }) =>
                            callStroageLocation({ searchQuery, refHazId: record.refHaz?.id, refUserCompany: props.hazMgt.refTo?.id }),
                        optLabel: "name",
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refStorageLocation = value;
                            record.refStorageLocationSub = null;
                            record.refStorageLocationSubItem = null;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false,
            width: 200
        })
    );
    columns.push(
        createColumnInlineSelect({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refStorageLocationSub"),
            dataIndex: "refStorageLocationSub",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || !record.refStorageLocation,
                        list: ({ searchQuery }) =>
                            callStroageLocationSub({
                                searchQuery,
                                refStorageLocationId: record.refStorageLocation?.id,
                                refHazId: record.refHaz?.id
                            }),
                        optLabel: "buildingName",
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refStorageLocationSub = value;
                            record.refStorageLocationSubItem = null;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false,
            width: 200
        })
    );
    columns.push(
        createColumnInlineSelect({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.refStorageLocationSubItem"),
            dataIndex: "refStorageLocationSubItem",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ list: () => [] }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || !record.refStorageLocationSub,
                        list: ({ searchQuery }) =>
                            callStroageLocationSubItem({
                                searchQuery,
                                refStorageLocationSubId: record.refStorageLocationSub?.id,
                                refHazId: record.refHaz?.id,
                                // notIds: items.filter(d => d.isDelete !== true).map(d => d.refStorageLocationSubItem?.id).filter(d => d),
                                notIds: [],
                            }),
                        renderLabel: ({ item }) => (
                            <span>
                                {item.lot} : ({item.refHazLicense?.refOperation?.value})
                            </span>
                        ),
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.refStorageLocationSubItem = value;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false,
            width: 250
        })
    );
    columns.push(
        createColumnInputNumber({
            title: () => i18n.global.t("views.manufacture.itemBalance"),
            dataIndexValue: ({ record }) => {
                const item = record.refStorageLocationSubItem;
                if (!item) return null;
                return item.amount - item.reserveOut;
            },
            customRender: ({ record, column }) => column.dataIndexValue({ record }),
            allowPrecision: true
        })
    );
    columns.push(
        createColumnInputNumber({
            title: () => i18n.global.t("views.manufacture.itemCal"),
            dataIndexValue: ({ record }) => {
                let result = 0;
                const amount = props.hazMgtStorageLocation.amount;
                const intense = record.intense;
                if (amount?.toString()?.length > 0 && intense?.toString()?.length > 0) {
                    result = ((parseFloat(amount) * parseFloat(intense)) / 100.0).toFixed(2);
                }
                return result;
            },
            customRender: param => {
                const { record, index } = param;
                const records = sortBy({ list: items, sort: data.page.sort });
                const recordBefore = records[index - 1];
                if (recordBefore && recordBefore.refHazSubStance.id === record.refHazSubStance.id) {
                    return null;
                }
                return <Number {...param.column} {...param} text={param.column.dataIndexValue({ record })}></Number>;
            },
            sorter: false,
            allowPrecision: true
        })
    );
    columns.push(
        createColumnInlineInputNumber({
            title: () => i18n.global.t("views.manufacture.itemUse"),
            dataIndex: "amount",
            items,
            useForm,
            itemsKey,
            inputProp: () => ({ allowPrecision: true }),
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly || (record.refHazFrom?.key === "1" && !record.refStorageLocationSubItem),
                        allowPrecision: true,
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.amount = value;
                        },
                        onBlur: () => useForm.validate(items.map((d, index) => `${itemsKey}.${index}.amount`)).catch(() => {})
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false
        })
    );
    columns.push(
        createColumnInlineTextArea({
            title: () => i18n.global.t("model.hazMgtStorageLocationStore.remark"),
            dataIndex: "remark",
            items,
            useForm,
            itemsKey,
            customRenderInputProp: ({ record, key }) =>
                lodash.merge(
                    {
                        disabled: viewOnly,
                        "onUpdate:value": value => {
                            props.hazMgtStorageLocation.isSave = true;
                            record.remark = value;
                        }
                    },
                    conditionFormChecker({ props, data, executeKey: "inputPropField", params: { field: { key } } })
                ),
            sorter: false
        })
    );
    return columns;
};

export const initData = ({ props, data }) => {
    const { menuContext, viewOnly, itemsKey, canDelete } = props;
    const { useForm } = data;
    const { modelRef } = useForm;
    const items = itemsKey ? lodash.get(modelRef, itemsKey) : modelRef;
    data.columns = createColumns({ props, data });
    data.page = createPage({
        data: items,
        options: {
            sort: [
                { name: "refHazSubStance.name", type: "ASC" },
                { name: "createDate", type: "ASC" }
            ]
        }
    });
    data.tableOptions = { search: { activeKey: null } };
    data.manages = [
        createManageDelete({
            menuContext,
            visible: () => (viewOnly ? false : canDelete),
            onClick: ({ rowSelection }) => {
                onClickDeleteAllClient({ rowSelection, useForm, items, key: itemsKey });
                data.page.page = 0;
            },
            getCheckboxProps: ({ record }) => {
                let classes = [];
                const records = sortBy({ list: items, sort: data.page.sort });
                const index = records.findIndex(d => d._id === record._id);
                const recordBefore = records[index - 1];
                if (!((recordBefore && recordBefore.refHazSubStance.id === record.refHazSubStance.id) || viewOnly)) {
                    classes.push("d-none");
                }
                return { disabled: record.isActive === true, class: classes.join(" ") };
            }
        }),
        createManageExportCsv({ menuContext })
    ];
    data.manageExtras = [
        /*
        {
            render: () => createButtonCreate({ onClick: () => items.push({}) }),
            visible: () => (viewOnly ? false : canCreate)
        }
        */
    ];
    data.rowActions = [];
    fetchData({ props, data }).then(page => items.push(...page.data));
};

export const fetchData = ({ props }) => {
    const { fetchCriteria } = props;
    if (!fetchCriteria || fetchCriteria.fetched) {
        return Promise.resolve({ data: [] });
    }
    fetchCriteria.fetched = true;
    setIsLoading(true);
    return callSearch({ criteria: fetchCriteria }).finally(() => setIsLoading(false));
};
